import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Dialog,
    Divider,
    IconButton,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { outlinedFieldProps } from '../utils/fieldStyles';
import {
    tripHighlightImageHint,
    validateTripHighlightImage,
} from '../../utils/tripImageDimensions';
import AddressAutocompleteInput from '../fields/AddressAutocompleteInput';


const AddHighlightDialog = ({ open, onClose, onSave, initialHighlight = null, initialFile = null }) => {
    const inputRef = useRef(null);
    const ownedPreviewUrlRef = useRef(null);
    const isEditing = Boolean(initialHighlight?.id);

    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [isValidating, setIsValidating] = useState(false);

    const revokeOwnedPreviewUrl = () => {
        if (ownedPreviewUrlRef.current) {
            URL.revokeObjectURL(ownedPreviewUrlRef.current);
            ownedPreviewUrlRef.current = null;
        }
    };

    useEffect(() => {
        if (!open) return;
        revokeOwnedPreviewUrl();
        setName(initialHighlight?.name || '');
        setLocation(initialHighlight?.location || '');
        setErrors({});
        setIsValidating(false);

        if (initialFile && !initialHighlight) {
            (async () => {
                const result = await validateTripHighlightImage(initialFile);
                if (!result.ok) {
                    setErrors({ image: result.message });
                    setPendingFile(null);
                    setPreviewUrl('');
                    return;
                }
                const nextPreviewUrl = URL.createObjectURL(initialFile);
                ownedPreviewUrlRef.current = nextPreviewUrl;
                setPendingFile(initialFile);
                setPreviewUrl(nextPreviewUrl);
            })();
        } else {
            setPreviewUrl(initialHighlight?.image || '');
            setPendingFile(initialHighlight?.pendingFile || null);
        }
    }, [open, initialHighlight, initialFile]);

    const handleCancel = () => {
        revokeOwnedPreviewUrl();
        onClose();
    };

    const handleImageSelect = async (file) => {
        if (!file) return;
        setIsValidating(true);
        try {
            const result = await validateTripHighlightImage(file);
            if (!result.ok) {
                setErrors((prev) => ({ ...prev, image: result.message }));
                return;
            }
            revokeOwnedPreviewUrl();
            const nextPreviewUrl = URL.createObjectURL(file);
            ownedPreviewUrlRef.current = nextPreviewUrl;
            setPendingFile(file);
            setPreviewUrl(nextPreviewUrl);
            setErrors((prev) => ({ ...prev, image: '' }));
        } finally {
            setIsValidating(false);
        }
    };

    const validate = () => {
        const next = {};
        if (!previewUrl) next.image = 'Image is required';
        if (!name.trim()) next.name = 'Highlight name is required';
        if (!location.trim()) next.location = 'Location is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onSave({
            id: initialHighlight?.id || `hl-${Date.now()}`,
            name: name.trim(),
            location: location.trim(),
            image: previewUrl,
            pendingFile,
            image_attachment_id: initialHighlight?.image_attachment_id || null,
        });
        // Transfer blob ownership to the highlight card — do not revoke here.
        ownedPreviewUrlRef.current = null;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    maxHeight: 'calc(100dvh - 32px)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={3}
                py={2}
                flexShrink={0}
            >
                <MDTypography variant="h6" fontWeight="bold" color="info">
                    {isEditing ? 'Edit Highlight' : 'Add Highlight'}
                </MDTypography>
                <IconButton size="small" onClick={handleCancel} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </MDBox>

            <Divider flexItem />

            <MDBox
                px={3}
                py={2.5}
                display="flex"
                flexDirection="column"
                gap={2}
                sx={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}
            >
                <Box
                    onClick={() => !isValidating && inputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        if (!isValidating) {
                            handleImageSelect(e.dataTransfer.files?.[0]);
                        }
                    }}
                    sx={{
                        border: '2px dashed',
                        borderColor: errors.image ? 'error.main' : 'grey.300',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: isValidating ? 'wait' : 'pointer',
                        bgcolor: previewUrl ? 'transparent' : 'grey.50',
                        backgroundImage: previewUrl ? `url(${previewUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        minHeight: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {!previewUrl && (
                        <>
                            <ImageOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                            <MDTypography variant="body2" color="text">
                                {isValidating ? 'Checking image…' : 'Click to Upload Image *'}
                            </MDTypography>
                        </>
                    )}
                </Box>
                <MDTypography variant="caption" color="text">
                    {tripHighlightImageHint}
                </MDTypography>
                {errors.image && (
                    <MDTypography variant="caption" color="error">{errors.image}</MDTypography>
                )}

                <TextField
                    {...outlinedFieldProps}
                    label="Highlight Name *"
                    placeholder="Enter name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={Boolean(errors.name)}
                    helperText={errors.name || ''}
                    fullWidth
                />

                <AddressAutocompleteInput
                    label="Location"
                    placeholder="Enter location"
                    value={location}
                    onChange={(next) => {
                        setLocation(next);
                        setErrors((prev) => ({ ...prev, location: '' }));
                    }}
                    error={Boolean(errors.location)}
                    errorMessage={errors.location}
                    required
                />
            </MDBox>

            <Divider flexItem />

            <MDBox
                display="flex"
                justifyContent="flex-end"
                gap={1}
                px={3}
                py={2}
                flexShrink={0}
            >
                <MDButton variant="outlined" color="secondary" onClick={handleCancel}>
                    Cancel
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={isValidating}
                >
                    {isEditing ? 'Save' : 'Add to List'}
                </MDButton>
            </MDBox>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    handleImageSelect(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
        </Dialog>
    );
};

AddHighlightDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    initialHighlight: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        location: PropTypes.string,
        image: PropTypes.string,
        pendingFile: PropTypes.object,
        image_attachment_id: PropTypes.number,
    }),
    initialFile: PropTypes.object,
};

export default AddHighlightDialog;
